import type { Process } from "../../api/types";
import type { ComparePair } from "./processCompareModel";
import type { ProcessComparison } from "./processComparison";
import { comparisonStepKey } from "./processComparison";

/** Keep app-qualified steps distinct, as in the existing run comparison. Across processes,
 * match exact normalized labels within the same app; never rely on process-local node IDs. */
export function comparisonGraphData(comparison: ProcessComparison) {
  const crossProcess = comparison.a.process.slug !== comparison.b.process.slug;
  const nodes = new Map<string, Process["nodes"][number]>();
  const groups = (["a", "b"] as const).map(side => {
    const group = comparison[side];
    const nodeIds = new Map<string, string>();
    for (const node of group.process.nodes) {
      const id = crossProcess
        ? JSON.stringify([comparisonStepKey(node.name), node.app ?? ""])
        : node.id;
      nodeIds.set(node.id, id);
      if (!nodes.has(id)) nodes.set(id, { ...node, id });
    }
    return group.runs.map(run => ({
      ...run,
      id: crossProcess ? `${side}:${run.id}` : run.id,
      steps: run.steps.map(step => ({
        ...step,
        nodeId: nodeIds.get(step.nodeId) ?? `${side}:${step.nodeId}`,
      })),
    }));
  });
  const [a, b] = groups;
  const instances = [...new Map([...a, ...b].map(run => [run.id, run])).values()];
  const process: Process = {
    ...comparison.a.process,
    nodes: [...nodes.values()],
    instances,
    sources: instances.map(run => run.source),
  };
  const pair: ComparePair = {
    aId: a[0].id,
    bId: b[0].id,
    aIds: a.map(run => run.id),
    bIds: b.map(run => run.id),
  };
  return { process, a, b, pair };
}
